import './chat-toolbar.scss';

import { useMutation } from '@apollo/client';
import { kebabCase } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { ConversationHealthProfessionalContextModal } from '../../compositions/conversations/modals/conversation-select-health-professional-modal/conversation-health-professional-modal';
import {
  ChatMessageSummaryItem,
  ConversationSummaryModal,
} from '../../compositions/conversations/modals/conversation-summary-modal/conversation-summary-modal';
import { HealthProfessionalButton } from '../../compositions/health-professionals/health-professional-button';
import { useAppContext } from '../../data/context/app-context';
import {
  ConversationItemDataFragment,
  ConversationStatus,
  UpdateConversationItemStatusDocument,
  UpdateConversationTitleDocument,
} from '../../data/types/api/graphql';
import {
  ContextMenuVerticalPosition,
  useContextMenu,
} from '../../data/utils/use-context-menu';
import { AlertMessageType } from '../alert-message/alert-message';
import { Button, ButtonIcon, ButtonType } from '../button/button';
import { ContextMenu } from '../context-menu/context-menu';
import { BaseSwitch } from '../form/input/base-switch';
import { InputField } from '../form/input/input-field';
import { CustomFormattedMessage } from '../intl/custom-formatted-message';
import { RelativeTime } from '../utils/relative-time/relative-time';
import { ChatPatient } from './chat-patient';

type ChatToolbarProps = {
  healthProviderId: string | null | undefined;
  conversationItem: ConversationItemDataFragment | null | undefined;
  selectedChatMessageItems: ChatMessageSummaryItem[];
};

export function ChatToolbar({
  healthProviderId,
  conversationItem,
  selectedChatMessageItems,
}: ChatToolbarProps) {
  const intl = useIntl();

  const [summaryModalOpen, setSummaryModalOpen] = useState<boolean>(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const titleRowRef = useRef<HTMLDivElement>(null);
  const onSubmitTitleRef = useRef<() => void>(() => {});

  const { showAlertMessage } = useAppContext();

  const [
    updateConversationItemStatus,
    { loading: updateConversationItemStatusLoading },
  ] = useMutation(UpdateConversationItemStatusDocument);

  const [updateConversationTitle, { loading: updateConversationTitleLoading }] =
    useMutation(UpdateConversationTitleDocument);

  useEffect(() => {
    setIsEditingTitle(false);
  }, [conversationItem?.id]);

  const onStartEditTitle = useCallback(() => {
    setEditTitleValue(conversationItem?.title ?? '');
    setIsEditingTitle(true);
  }, [conversationItem?.title]);

  const onCancelEditTitle = useCallback(() => {
    setIsEditingTitle(false);
  }, []);

  const onSubmitTitle = useCallback(async () => {
    if (!conversationItem?.id) {
      setIsEditingTitle(false);
      return;
    }
    if (editTitleValue === conversationItem.title) {
      setIsEditingTitle(false);
      return;
    }
    try {
      await updateConversationTitle({
        variables: {
          conversationId: conversationItem.id,
          title: editTitleValue,
        },
      });
      showAlertMessage({
        type: AlertMessageType.SUCCESS,
        message: intl.formatMessage({
          id: 'conversation.conversation-title.success',
        }),
      });
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    } finally {
      setIsEditingTitle(false);
    }
  }, [
    conversationItem?.id,
    conversationItem?.title,
    editTitleValue,
    intl,
    showAlertMessage,
    updateConversationTitle,
  ]);

  onSubmitTitleRef.current = onSubmitTitle;

  useEffect(() => {
    if (!isEditingTitle) return undefined;
    const input = titleRowRef.current?.querySelector<HTMLInputElement>('input');
    if (!input) return undefined;
    input.focus();
    input.select();
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter') onSubmitTitleRef.current();
      else if (e.key === 'Escape') setIsEditingTitle(false);
    };
    input.addEventListener('keydown', handler);
    return () => input.removeEventListener('keydown', handler);
  }, [isEditingTitle]);

  const onHandleStatus = useCallback(
    async (value: ConversationStatus) => {
      try {
        const result = await updateConversationItemStatus({
          variables: {
            id: conversationItem?.id ?? '',
            status: value,
          },
        });

        if (result.data?.conversationStatus) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'conversation.conversation-status.success',
            }),
          });
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    },
    [conversationItem?.id, intl, showAlertMessage, updateConversationItemStatus]
  );

  const onHandleUrgency = useCallback(
    async (value: boolean) => {
      try {
        const result = await updateConversationItemStatus({
          variables: {
            id: conversationItem?.id ?? '',
            status: conversationItem?.status ?? ConversationStatus.Active,
            urgent: value,
          },
        });

        if (result.data?.conversationStatus) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'conversation.conversation-urgency.success',
            }),
          });
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    },
    [
      conversationItem?.id,
      conversationItem?.status,
      intl,
      showAlertMessage,
      updateConversationItemStatus,
    ]
  );

  const statuses = [
    ConversationStatus.WaitList,
    ConversationStatus.Active,
    ConversationStatus.Monitored,
    ConversationStatus.Reminder,
    ConversationStatus.OnHold,
    ConversationStatus.Finished,
  ];

  const {
    contextMenuState: statusContextMenuState,
    onOpenContextMenu: onOpenStatusContextMenu,
  } = useContextMenu({
    center: false,
    vertical: ContextMenuVerticalPosition.Bottom,
  });

  const {
    contextMenuState: healthProfessionalContextMenuState,
    onOpenContextMenu: onOpenHealthProfessionalContextMenu,
    onCloseContextMenu: onCloseHealthProfessionalContextMenu,
  } = useContextMenu();

  return (
    <>
      <div className="chat-toolbar">
        {conversationItem && (
          <>
            <div className="chat-toolbar__top">
              <div className="chat-toolbar__top__content">
                <ChatPatient patient={conversationItem.patient} />
              </div>
              <div className="chat-toolbar__top__actions">
                <Button
                  testId="chat-summary"
                  onClick={() => setSummaryModalOpen(true)}
                >
                  <CustomFormattedMessage id="chat.summary" />
                </Button>
                <HealthProfessionalButton
                  testId="chat-health-professional"
                  healthProfessional={conversationItem.healthProfessional}
                  onClick={onOpenHealthProfessionalContextMenu}
                />
                <div
                  className={`urgent-toggle${conversationItem.urgent ? ' urgent-toggle--urgent' : ''}`}
                >
                  <BaseSwitch
                    testId="chat-urgency"
                    className="red"
                    title={<CustomFormattedMessage id="common.urgent" />}
                    value={conversationItem.urgent || false}
                    disabled={updateConversationItemStatusLoading}
                    onChange={onHandleUrgency}
                  />
                </div>
                <Button
                  testId="chat-status"
                  className={kebabCase(conversationItem.status)}
                  disabled={updateConversationItemStatusLoading}
                  toggle
                  onClick={onOpenStatusContextMenu}
                >
                  <CustomFormattedMessage
                    id={`common.conversation.${kebabCase(
                      conversationItem.status
                    )}`}
                  />
                </Button>
              </div>
            </div>

            <div className="chat-toolbar__title">
              <div ref={titleRowRef} className="chat-toolbar__title__row">
                {isEditingTitle ? (
                  <InputField
                    name="conversation-title"
                    value={editTitleValue}
                    disabled={updateConversationTitleLoading}
                    onChange={v => setEditTitleValue(v)}
                  />
                ) : (
                  <h2 title={conversationItem.title}>
                    {conversationItem.title}
                  </h2>
                )}
                {isEditingTitle ? (
                  <>
                    <Button
                      testId="chat-title-save"
                      type={ButtonType.ICON_ONLY}
                      icon={
                        updateConversationTitleLoading
                          ? ButtonIcon.LOADING
                          : ButtonIcon.CHECK
                      }
                      disabled={updateConversationTitleLoading}
                      onClick={onSubmitTitle}
                    />
                    <Button
                      testId="chat-title-cancel"
                      type={ButtonType.ICON_ONLY}
                      icon={ButtonIcon.CLOSE}
                      disabled={updateConversationTitleLoading}
                      onClick={onCancelEditTitle}
                    />
                  </>
                ) : (
                  <Button
                    testId="chat-title-edit"
                    type={ButtonType.ICON_ONLY}
                    icon={ButtonIcon.EDIT}
                    onClick={onStartEditTitle}
                  />
                )}
              </div>
              <p className="info">
                <RelativeTime value={conversationItem.lastUpdated} />
              </p>
            </div>
          </>
        )}
      </div>

      {statusContextMenuState && (
        <ContextMenu
          testId="chat-status"
          state={statusContextMenuState}
          items={statuses.map(status => ({
            key: status,
            title: (
              <CustomFormattedMessage
                id={`common.conversation.${kebabCase(status)}`}
              />
            ),
            disabled: updateConversationItemStatusLoading,
            onClick: () => {
              onHandleStatus(status);
            },
          }))}
        />
      )}

      {healthProfessionalContextMenuState && conversationItem && (
        <ConversationHealthProfessionalContextModal
          state={healthProfessionalContextMenuState}
          healthProviderId={healthProviderId}
          conversationItem={conversationItem}
          onClose={onCloseHealthProfessionalContextMenu}
        />
      )}

      {summaryModalOpen && conversationItem && (
        <ConversationSummaryModal
          conversationItem={conversationItem}
          selectedChatMessageItems={selectedChatMessageItems}
          onClose={() => setSummaryModalOpen(false)}
        />
      )}
    </>
  );
}
